"use client";

import type { PropsWithChildren } from "react";

import { createContext, useEffect, useState, Suspense } from "react";
import { useContext } from "react";

import { useSearchParamsState, type UpdateSearchParams } from "@ui/hooks/useSearchParamsState";

import type { Section } from "./pricing-sections";
import type { BillingCycle, PlanType, PlatformVariant } from "./PricingControlsV3";

const PRICING_TABLE_HASH = "pricing-table";
const PRICING_TABLE_STATE_KEY = "price";

type TextToSpeechSectionData = {
  selectedModel: "flash_turbo" | "highest_quality";
};

type SpeechToTextSectionData = {
  interface: "api" | "ui";
  version: "v1" | "realtime_v2";
};

type DubbingSectionData = {
  product: "automatic_dubbing" | "dubbing_studio";
  watermark: "no_watermark" | "watermark";
};

type ProductionsSectionData = {
  selectedView: "enterprise" | "free";
};

type SectionData = {
  dubbing: DubbingSectionData;
  productions: ProductionsSectionData;
  speech_to_text: SpeechToTextSectionData;
  text_to_speech: TextToSpeechSectionData;
};

// URL state type - all fields are optional for partial updates
type PricingTableUrlState = {
  section?: Section;
  sections?: string; // Comma-separated list of sections for V3 table
  unit?: "characters" | "minutes";
  offer?: string;
  api?: "true" | "false";
  model?: "flash_turbo" | "highest_quality";
  stt_version?: "v1" | "realtime_v2";
  stt_interface?: "api" | "ui";
  dubbing_product?: "automatic_dubbing" | "dubbing_studio";
  dubbing_watermark?: "watermark" | "no_watermark";
  productions_view?: "free" | "enterprise";
  platform?: PlatformVariant;
  billing_cycle?: BillingCycle;
  plan_type?: PlanType;
};

interface PricingTableContextData {
  displayUnit: "characters" | "minutes";
  isApiPricingOpened: boolean;
  sectionData: SectionData;
  selectedOffer: string;
  selectedSection: Section;
  expandedSections: Set<Section>; // In the v3 table, we can have multiple sections expanded at once
  // Pricing controls state
  selectedPlatform: PlatformVariant;
  billingCycle: BillingCycle;
  planType: PlanType;
  setDisplayUnit: (displayUnit: "characters" | "minutes") => void;
  setIsApiPricingOpened: (isApiPricingOpened: boolean) => void;
  setSectionData: (sectionData: SectionData) => void;
  setSelectedOffer: (offer: string) => void;
  setSelectedSection: (section: Section) => void;
  setExpandedSections: (sections: Set<Section>) => void;
  setSelectedPlatform: (platform: PlatformVariant) => void;
  setBillingCycle: (cycle: BillingCycle) => void;
  setPlanType: (type: PlanType) => void;
}

export const DEFAULT_CONTEXT_DATA: PricingTableContextData = {
  isApiPricingOpened: false,
  selectedSection: "text_to_speech",
  expandedSections: new Set(["text_to_speech"]),
  selectedPlatform: "all",
  billingCycle: "monthly",
  planType: "individual",
  setIsApiPricingOpened: () => {},
  setSelectedSection: () => {},
  setExpandedSections: () => {},
  displayUnit: "minutes",
  setDisplayUnit: () => {},
  selectedOffer: "free",
  setSelectedOffer: () => {},
  sectionData: {
    speech_to_text: { interface: "api", version: "v1" },
    text_to_speech: { selectedModel: "flash_turbo" },
    dubbing: { product: "automatic_dubbing", watermark: "watermark" },
    productions: { selectedView: "free" },
  },
  setSectionData: () => {},
  setSelectedPlatform: () => {},
  setBillingCycle: () => {},
  setPlanType: () => {},
};

const URL_PARAMS_DEFAULTS: Record<keyof PricingTableUrlState, unknown> = {
  section: DEFAULT_CONTEXT_DATA.selectedSection,
  sections: Array.from(DEFAULT_CONTEXT_DATA.expandedSections).join(","),
  unit: DEFAULT_CONTEXT_DATA.displayUnit,
  offer: DEFAULT_CONTEXT_DATA.selectedOffer,
  api: DEFAULT_CONTEXT_DATA.isApiPricingOpened ? "true" : "false",
  model: DEFAULT_CONTEXT_DATA.sectionData.text_to_speech.selectedModel,
  stt_version: DEFAULT_CONTEXT_DATA.sectionData.speech_to_text.version,
  stt_interface: DEFAULT_CONTEXT_DATA.sectionData.speech_to_text.interface,
  dubbing_product: DEFAULT_CONTEXT_DATA.sectionData.dubbing.product,
  dubbing_watermark: DEFAULT_CONTEXT_DATA.sectionData.dubbing.watermark,
  productions_view: DEFAULT_CONTEXT_DATA.sectionData.productions.selectedView,
  platform: DEFAULT_CONTEXT_DATA.selectedPlatform,
  billing_cycle: DEFAULT_CONTEXT_DATA.billingCycle,
  plan_type: DEFAULT_CONTEXT_DATA.planType,
};

type PricingTableContextProviderProps = {
  children: React.ReactNode;
  showApiPricing?: boolean;
  initialSelectedSection?: Section;
  initialPlatform?: PlatformVariant;
  initialBillingCycle?: BillingCycle;
  initialPlanType?: PlanType;
};

// Hook that manages pricing table state with optional URL syncing
function usePricingTableStateManagement({
  showApiPricing,
  initialSelectedSection,
  initialPlatform,
  initialBillingCycle,
  initialPlanType,
  getSearchParam,
  updateSearchParams,
}: {
  showApiPricing?: boolean;
  initialSelectedSection?: Section;
  initialPlatform?: PlatformVariant;
  initialBillingCycle?: BillingCycle;
  initialPlanType?: PlanType;
  getSearchParam?: <T = unknown>(key: string) => T | string | null;
  updateSearchParams?: UpdateSearchParams;
}) {
  // State initialized with defaults
  const [selectedSection, setSelectedSectionState] = useState<Section>(
    initialSelectedSection || DEFAULT_CONTEXT_DATA.selectedSection,
  );
  const [expandedSections, setExpandedSectionsState] = useState<Set<Section>>(
    new Set([initialSelectedSection || DEFAULT_CONTEXT_DATA.selectedSection]),
  );
  const [displayUnit, setDisplayUnitState] = useState(DEFAULT_CONTEXT_DATA.displayUnit);
  const [selectedOffer, setSelectedOfferState] = useState(DEFAULT_CONTEXT_DATA.selectedOffer);
  const [isApiPricingOpened, setIsApiPricingOpenedState] = useState(showApiPricing || false);
  const [sectionData, setSectionDataState] = useState(DEFAULT_CONTEXT_DATA.sectionData);
  const [selectedPlatform, setSelectedPlatformState] = useState<PlatformVariant>(
    initialPlatform || DEFAULT_CONTEXT_DATA.selectedPlatform,
  );
  const [billingCycle, setBillingCycleState] = useState<BillingCycle>(
    initialBillingCycle || DEFAULT_CONTEXT_DATA.billingCycle,
  );
  const [planType, setPlanTypeState] = useState<PlanType>(initialPlanType || DEFAULT_CONTEXT_DATA.planType);

  // Sync from URL on mount (only if getSearchParam provided)
  useEffect(() => {
    if (!getSearchParam) return;

    const urlState = getSearchParam<PricingTableUrlState>(PRICING_TABLE_STATE_KEY);
    if (!urlState || typeof urlState === "string") return;

    if (urlState.section) setSelectedSectionState(urlState.section);

    // Handle multiple expanded sections (V3 table)
    if (urlState.sections) {
      const sections = urlState.sections.split(",").filter(Boolean) as Section[];
      setExpandedSectionsState(new Set(sections));
    } else if (urlState.section) {
      // Backwards compatibility: if only section is set, use it for expandedSections too
      setExpandedSectionsState(new Set([urlState.section]));
    }

    if (urlState.unit) setDisplayUnitState(urlState.unit);
    if (urlState.offer) setSelectedOfferState(urlState.offer);
    if (urlState.api !== undefined) setIsApiPricingOpenedState(urlState.api === "true");
    if (urlState.platform) setSelectedPlatformState(urlState.platform);
    if (urlState.billing_cycle) setBillingCycleState(urlState.billing_cycle);
    if (urlState.plan_type) setPlanTypeState(urlState.plan_type);

    const { model, stt_version, stt_interface, dubbing_product, dubbing_watermark, productions_view } = urlState;

    if (model || stt_version || stt_interface || dubbing_product || dubbing_watermark || productions_view) {
      setSectionDataState(prev => ({
        text_to_speech: {
          selectedModel: (model || prev.text_to_speech.selectedModel) as "flash_turbo" | "highest_quality",
        },
        speech_to_text: {
          version: (stt_version || prev.speech_to_text.version) as "v1" | "realtime_v2",
          interface: (stt_interface || prev.speech_to_text.interface) as "api" | "ui",
        },
        dubbing: {
          product: (dubbing_product || prev.dubbing.product) as "automatic_dubbing" | "dubbing_studio",
          watermark: (dubbing_watermark || prev.dubbing.watermark) as "watermark" | "no_watermark",
        },
        productions: { selectedView: (productions_view || prev.productions.selectedView) as "free" | "enterprise" },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to update URL state with partial updates
  // Automatically removes values that match defaults to keep URLs clean
  const updateUrlState = (
    updates: Partial<PricingTableUrlState>,
    options?: { replaceAll?: boolean; removeHash?: boolean },
  ) => {
    if (!getSearchParam || !updateSearchParams) return;

    let mergedState: Partial<PricingTableUrlState>;

    if (options?.replaceAll) {
      // Replace all state with only the updates
      mergedState = { ...updates };
    } else {
      // Merge with current state
      const currentState = getSearchParam<PricingTableUrlState>(PRICING_TABLE_STATE_KEY);
      mergedState = currentState && typeof currentState !== "string" ? { ...currentState, ...updates } : { ...updates };
    }

    // Remove values that match defaults
    for (const [key, value] of Object.entries(mergedState) as [keyof PricingTableUrlState, unknown][]) {
      // Always keep "section" in the URL, because it looks better
      if (key === "section") continue;

      if (value === URL_PARAMS_DEFAULTS[key]) {
        delete mergedState[key];
      }
    }

    updateSearchParams(
      {
        [PRICING_TABLE_STATE_KEY]: mergedState,
      },
      options?.removeHash ? { preserveHash: false } : { hash: PRICING_TABLE_HASH },
    );
  };

  // Setters - update URL if updateSearchParams is provided
  const setSelectedSection = (section: Section) => {
    setSelectedSectionState(section);
    updateUrlState({ section });
  };

  const setExpandedSections = (sections: Set<Section>) => {
    setExpandedSectionsState(sections);
    const sectionsArray = Array.from(sections);
    updateUrlState({
      sections: sectionsArray.length > 0 ? sectionsArray.join(",") : undefined,
      section: sectionsArray[0], // Keep backwards compatible single section param
    });
  };

  const setDisplayUnit = (unit: "characters" | "minutes") => {
    setDisplayUnitState(unit);
    updateUrlState({ unit });
  };

  const setSelectedOffer = (offer: string) => {
    setSelectedOfferState(offer);
    updateUrlState({ offer });
  };

  const setIsApiPricingOpened = (opened: boolean) => {
    setIsApiPricingOpenedState(opened);
    updateUrlState({ api: opened ? "true" : "false" });
  };

  const setSectionData = (data: SectionData) => {
    setSectionDataState(data);
    updateUrlState({
      model: data.text_to_speech.selectedModel,
      stt_version: data.speech_to_text.version,
      stt_interface: data.speech_to_text.interface,
      dubbing_product: data.dubbing.product,
      dubbing_watermark: data.dubbing.watermark,
      productions_view: data.productions.selectedView,
    });
  };

  const setSelectedPlatform = (platform: PlatformVariant) => {
    setSelectedPlatformState(platform);
    // Reset all other URL params when changing platform, and remove any existing hash
    updateUrlState({ platform }, { replaceAll: true, removeHash: true });
  };

  const setBillingCycle = (cycle: BillingCycle) => {
    setBillingCycleState(cycle);
    updateUrlState({ billing_cycle: cycle });
  };

  const setPlanType = (type: PlanType) => {
    setPlanTypeState(type);
    updateUrlState({ plan_type: type });
  };

  return {
    selectedSection,
    setSelectedSection,
    expandedSections,
    setExpandedSections,
    isApiPricingOpened,
    setIsApiPricingOpened,
    displayUnit,
    setDisplayUnit,
    sectionData,
    setSectionData,
    selectedOffer,
    setSelectedOffer,
    selectedPlatform,
    setSelectedPlatform,
    billingCycle,
    setBillingCycle,
    planType,
    setPlanType,
  };
}

// Base provider without URL syncing - used for SSR and Suspense fallback
const PricingTableContextProviderBase = ({
  children,
  showApiPricing,
  initialSelectedSection,
  initialPlatform,
  initialBillingCycle,
  initialPlanType,
}: PropsWithChildren & PricingTableContextProviderProps) => {
  const contextValue = usePricingTableStateManagement({
    showApiPricing,
    initialSelectedSection,
    initialPlatform,
    initialBillingCycle,
    initialPlanType,
  });

  return <PricingTableContext.Provider value={contextValue}>{children}</PricingTableContext.Provider>;
};

// Provider with URL syncing - uses getSearchParam and updateSearchParams
const PricingTableContextProviderWithUrl = ({
  children,
  showApiPricing,
  initialSelectedSection,
  initialPlatform,
  initialBillingCycle,
  initialPlanType,
}: PropsWithChildren & PricingTableContextProviderProps) => {
  const { getSearchParam, updateSearchParams } = useSearchParamsState();

  const contextValue = usePricingTableStateManagement({
    showApiPricing,
    initialSelectedSection,
    initialPlatform,
    initialBillingCycle,
    initialPlanType,
    getSearchParam,
    updateSearchParams,
  });

  return <PricingTableContext.Provider value={contextValue}>{children}</PricingTableContext.Provider>;
};

export const PricingTableContextProvider = (props: PropsWithChildren & PricingTableContextProviderProps) => {
  return (
    <Suspense fallback={<PricingTableContextProviderBase {...props} />}>
      <PricingTableContextProviderWithUrl {...props} />
    </Suspense>
  );
};

export const PricingTableContext = createContext<PricingTableContextData>(DEFAULT_CONTEXT_DATA);

export const usePricingTableContext = () => {
  const context = useContext(PricingTableContext);
  if (!context) {
    throw new Error("usePricingTableContext must be used within a PricingTableContextProvider");
  }
  return context;
};
